import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTokens
} from '@barracuda-internal/bds-core-v2'
import { useFormatMessage } from 'lib/localization'

const BASE_I18N_KEY = 'ess_wizard.verify_domain.confirm_dialog'
const MAX_DOMAINS_SHOWN = 5

export interface ConfirmInlineDeploymentDialogProps {
  domains: string[]
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmInlineDeploymentDialog: React.FC<ConfirmInlineDeploymentDialogProps> = ({
  domains,
  onConfirm,
  onCancel
}) => {
  const tokens = useTokens()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const [showAll, setShowAll] = useState(false)

  const hasMoreDomains = domains.length > MAX_DOMAINS_SHOWN
  const displayedDomains = showAll ? domains : domains.slice(0, MAX_DOMAINS_SHOWN)
  const remainingCount = domains.length - MAX_DOMAINS_SHOWN

  return (
    <Dialog open bdsDialogPadding="spacious" bdsDialogWidth="medium" onClose={onCancel} sx={{ zIndex: 3005 }}>
      <DialogTitle onClose={onCancel} showCloseIcon title={formatMessage('title')} />
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.aliasesSpaceContainerDefault
          }}
        >
          <Typography
            sx={{
              fontFamily: tokens.aliasesFontFamilyDefault,
              fontSize: tokens.aliasesFontSizeDefault,
              fontWeight: tokens.aliasesFontWeightDefault,
              color: tokens.aliasesColorTextStaticEmphasis,
              lineHeight: tokens.aliasesLineHeightDefault
            }}
          >
            {formatMessage('content', { count: domains.length })}
          </Typography>
          <Box
            sx={{
              maxHeight: showAll ? '300px' : 'auto',
              overflowY: showAll ? 'auto' : 'visible'
            }}
          >
            {displayedDomains.map(domain => (
              <Typography
                key={domain}
                sx={{
                  fontFamily: tokens.aliasesFontFamilyDefault,
                  fontSize: tokens.aliasesFontSizeDefault,
                  fontWeight: tokens.aliasesFontWeightDefault,
                  color: tokens.aliasesColorTextStaticEmphasis,
                  lineHeight: tokens.aliasesLineHeightDefault,
                  paddingLeft: tokens.aliasesSpaceContainerDefault
                }}
              >
                {`• ${domain}`}
              </Typography>
            ))}
            {hasMoreDomains && !showAll && (
              <Typography
                sx={{
                  fontFamily: tokens.aliasesFontFamilyDefault,
                  fontSize: tokens.aliasesFontSizeDefault,
                  fontWeight: tokens.aliasesFontWeightDefault,
                  color: tokens.aliasesColorTextStaticSubtle,
                  lineHeight: tokens.aliasesLineHeightDefault,
                  paddingLeft: tokens.aliasesSpaceContainerDefault
                }}
              >
                {formatMessage('and_more', { count: remainingCount })}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {hasMoreDomains && !showAll ? (
          <Button bdsType="interactiveSubtle" onClick={() => setShowAll(true)}>
            {formatMessage('view_all')}
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: tokens.aliasesSpaceContainerDefault }}>
          <Button bdsType="interactiveSubtle" onClick={onCancel}>
            {formatMessage('cancel')}
          </Button>
          <Button bdsType="interactiveEmphasis" onClick={onConfirm}>
            {formatMessage('confirm')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmInlineDeploymentDialog
